import api from './api';

export const questionBankService = {
    // Listar todas as questões do banco
    list: async (flowId = null) => {
        try {
            console.log('Buscando lista de questões do banco');
            let url = '/question-bank';
            
            // Se flowId for fornecido, adiciona como parâmetro de consulta
            if (flowId) {
                url += `?flow_id=${flowId}`;
                console.log(`Filtrando questões por fluxo ID: ${flowId}`);
            }
            
            const response = await api.get(url);
            
            if (!response.data) {
                console.error('Resposta vazia ao listar questões do banco');
                throw new Error('Resposta vazia ao listar questões do banco');
            }
            
            console.log('Questões do banco recebidas:', response.data);
            
            // Garantir que todos os campos necessários estejam presentes
            const processedQuestions = Array.isArray(response.data) ? response.data.map(q => ({
                ...q,
                options: q.options || [],
                is_final: q.is_final || false,
                is_success: q.is_success || false,
                final_message: q.final_message || '',
                article_reference: q.article_reference || '',
            })) : [];
            
            return processedQuestions;
        } catch (error) {
            console.error('Erro ao listar questões do banco:', error);
            throw error;
        }
    },

    // Obter uma questão específica do banco
    get: async (id) => {
        try {
            console.log(`Buscando questão do banco com ID: ${id}`);
            
            if (!id) {
                console.error('ID da questão não fornecido');
                throw new Error('ID da questão não fornecido');
            }
            
            const response = await api.get(`/question-bank/${id}`);
            
            if (!response.data) {
                console.error(`Resposta vazia ao obter questão do banco com ID ${id}`);
                throw new Error(`Questão com ID ${id} não encontrada`);
            }
            
            console.log(`Questão do banco com ID ${id} recebida:`, response.data);
            
            // Garantir que todos os campos necessários estejam presentes
            const processedQuestion = {
                ...response.data,
                options: response.data.options || [],
                is_final: response.data.is_final || false,
                is_success: response.data.is_success || false,
                final_message: response.data.final_message || '',
                article_reference: response.data.article_reference || '',
            };
            
            return processedQuestion;
        } catch (error) {
            console.error(`Erro ao obter questão do banco com ID ${id}:`, error);
            throw error;
        }
    },

    // Criar uma nova questão no banco
    create: async (questionData) => {
        try {
            const response = await api.post('/question-bank', questionData);
            return response.data;
        } catch (error) {
            console.error('Erro ao criar questão no banco:', error);
            throw error;
        }
    },

    // Atualizar uma questão existente no banco
    update: async (id, questionData) => {
        try {
            const response = await api.put(`/question-bank/${id}`, questionData);
            return response.data;
        } catch (error) {
            console.error(`Erro ao atualizar questão do banco com ID ${id}:`, error);
            throw error;
        }
    },

    // Excluir uma questão do banco
    delete: async (id) => {
        try {
            const response = await api.delete(`/question-bank/${id}`);
            return response.data;
        } catch (error) {
            console.error(`Erro ao excluir questão do banco com ID ${id}:`, error);
            throw error;
        }
    },

    // Listar questões de um fluxo específico
    getFlowQuestions: async (flowId) => {
        try {
            const response = await api.get(`/question-bank/flow/${flowId}`);
            return response.data;
        } catch (error) {
            console.error(`Erro ao listar questões do fluxo ${flowId}:`, error);
            throw error;
        }
    },

    // Adicionar uma questão do banco a um fluxo
    addQuestionToFlow: async (flowId, questionData) => {
        try {
            console.log(`Adicionando questão ao fluxo ${flowId}`, questionData);
            
            // Garantir que o flow_id esteja no corpo da requisição
            if (!questionData.flow_id) {
                questionData.flow_id = flowId;
            }
            
            // Verificar se o flowId é válido
            if (!flowId || flowId === 'undefined' || flowId === 'null') {
                throw new Error('ID do fluxo inválido. Salve o fluxo antes de adicionar questões.');
            }
            
            const response = await api.post(`/question-bank/flow/${flowId}/add`, questionData);
            console.log('Resposta da API:', response.data);
            return response.data;
        } catch (error) {
            console.error(`Erro ao adicionar questão ao fluxo ${flowId}:`, error);
            throw error;
        }
    },

    // Atualizar a configuração de uma questão em um fluxo
    updateFlowQuestion: async (flowId, flowQuestionId, questionData) => {
        try {
            const response = await api.put(`/question-bank/flow/${flowId}/question/${flowQuestionId}`, questionData);
            return response.data;
        } catch (error) {
            console.error(`Erro ao atualizar questão ${flowQuestionId} no fluxo ${flowId}:`, error);
            throw error;
        }
    },

    // Remover uma questão de um fluxo
    removeQuestionFromFlow: async (flowId, flowQuestionId) => {
        try {
            console.log(`Removendo questão ${flowQuestionId} do fluxo ${flowId}`);
            
            // Verificar se o flowId é válido
            if (!flowId || flowId === 'undefined' || flowId === 'null') {
                throw new Error('ID do fluxo inválido. Salve o fluxo antes de remover questões.');
            }
            
            // Verificar se o flowQuestionId é válido
            if (!flowQuestionId || flowQuestionId === 'undefined' || flowQuestionId === 'null') {
                throw new Error('ID da questão inválido.');
            }
            
            const response = await api.delete(`/question-bank/flow/${flowId}/question/${flowQuestionId}`);
            console.log('Resposta da API:', response.data);
            return response.data;
        } catch (error) {
            console.error(`Erro ao remover questão ${flowQuestionId} do fluxo ${flowId}:`, error);
            throw error;
        }
    },

    // Obter a primeira questão de um fluxo
    getFirstQuestion: async (flowId) => {
        try {
            const response = await api.get(`/question-bank/flow/${flowId}/first`);
            return response.data;
        } catch (error) {
            console.error(`Erro ao obter primeira questão do fluxo ${flowId}:`, error);
            throw error;
        }
    },

    // Navegar para a próxima questão com base na opção selecionada
    navigateFlow: async (flowId, flowQuestionId, selectedOption) => {
        try {
            const response = await api.post(`/flows-new/${flowId}/navigate`, {
                flow_question_id: flowQuestionId,
                selected_option: selectedOption
            });
            return response.data;
        } catch (error) {
            console.error('Erro ao navegar no fluxo:', error);
            throw error;
        }
    },

    // Obter contagem de questões por fluxo
    getQuestionCountsByFlow: async () => {
        try {
            console.log('Buscando contagem de questões por fluxo');
            const response = await api.get('/question-bank/flow-counts');
            
            if (!response.data) {
                console.error('Resposta vazia ao buscar contagem de questões por fluxo');
                throw new Error('Resposta vazia ao buscar contagem de questões por fluxo');
            }
            
            console.log('Contagem de questões por fluxo recebida:', response.data);
            return response.data;
        } catch (error) {
            console.error('Erro ao buscar contagem de questões por fluxo:', error);
            throw error;
        }
    }
}; 